import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Clock, Save } from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function AdminHours() {
  const [hours, setHours] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/hours").then((r) => {
      const out = {};
      DAYS.forEach((d) => {
        out[d] = r.data.hours[d] || { open: "", close: "" };
      });
      setHours(out);
    });
  }, []);

  const update = (day, key, val) => setHours({ ...hours, [day]: { ...hours[day], [key]: val } });

  const toggleClosed = (day) => {
    const h = hours[day];
    if (h && h.open && h.close) {
      setHours({ ...hours, [day]: { open: "", close: "" } });
    } else {
      setHours({ ...hours, [day]: { open: "09:00", close: "17:00" } });
    }
  };

  const save = async () => {
    // Validate
    for (const day of DAYS) {
      const h = hours[day];
      if (h.open || h.close) {
        if (!h.open || !h.close) {
          toast.error(`${day}: please set both open and close times, or mark closed.`);
          return;
        }
        if (h.open >= h.close) {
          toast.error(`${day}: close time must be after open time.`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      const payload = { hours: {} };
      DAYS.forEach((d) => {
        payload.hours[d] = hours[d].open && hours[d].close ? { open: hours[d].open, close: hours[d].close } : { open: null, close: null };
      });
      await api.patch("/hours", payload);
      toast.success("Hours updated — live on the website");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  if (!hours) return <div className="p-8">Loading…</div>;

  return (
    <div data-testid="admin-hours-page">
      <h1 className="font-serif-display text-4xl text-[#5979A9] mb-2 flex items-center gap-2"><Clock size={26}/> Opening Hours</h1>
      <p className="text-[#4A5568] text-sm mb-7 max-w-2xl">Set the cafe opening times for each day. Changes are reflected on the website immediately and respected by the reservation system.</p>

      <div className="bg-white rounded-2xl border border-[#F0EAE6] p-2 max-w-2xl">
        {DAYS.map((day) => {
          const h = hours[day];
          const closed = !h.open || !h.close;
          return (
            <div key={day} className="grid grid-cols-[140px_1fr_1fr_100px] items-center gap-3 px-4 py-3 border-b border-[#F0EAE6] last:border-0" data-testid={`hours-day-${day.toLowerCase()}`}>
              <div className="font-semibold text-[#5979A9]">{day}</div>
              <input type="time" value={h.open || ""} disabled={closed}
                     onChange={(e) => update(day, "open", e.target.value)}
                     data-testid={`open-${day.toLowerCase()}`}
                     className="bg-transparent border-b-2 border-[#F0EAE6] focus:border-[#E05947] outline-none py-1.5 disabled:opacity-40"/>
              <input type="time" value={h.close || ""} disabled={closed}
                     onChange={(e) => update(day, "close", e.target.value)}
                     data-testid={`close-${day.toLowerCase()}`}
                     className="bg-transparent border-b-2 border-[#F0EAE6] focus:border-[#E05947] outline-none py-1.5 disabled:opacity-40"/>
              <button onClick={() => toggleClosed(day)} data-testid={`toggle-${day.toLowerCase()}`}
                      className={`text-xs px-3 py-1.5 rounded-full font-semibold ${closed ? "bg-[#E05947]/15 text-[#E05947]" : "bg-green-100 text-green-700"}`}>
                {closed ? "Closed" : "Open"}
              </button>
            </div>
          );
        })}
      </div>

      <button onClick={save} disabled={saving} data-testid="hours-save-button" className="btn-primary mt-6 inline-flex items-center gap-2">
        <Save size={16}/> {saving ? "Saving…" : "Save changes"}
      </button>
    </div>
  );
}
